import { useCallback, useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';
import { formatCurrencyFull, formatTimestamp } from '../../helpers/effectiveCapitalFormat';
import { LoaderSpinner } from '../LoaderSpinner';

function getFlexNextDueDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  if (day < 20) {
    return `${year}-${String(month + 1).padStart(2, '0')}-20`;
  }
  const nextMonth = month + 1 > 11 ? 0 : month + 1;
  const nextYear = month + 1 > 11 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-20`;
}

function formatShortDate(d) {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(parts[1], 10) - 1]} ${parseInt(parts[2], 10)}, ${parts[0]}`;
}

function StatementBalanceCell({ row, onSave }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);
  const isCreditType = row.type === 'credit' || row.subtype === 'line of credit';

  const startEdit = () => {
    setInputVal(row.statementBalance.toFixed(2));
    setEditing(true);
  };

  const handleSave = async () => {
    const num = parseFloat(inputVal.replace(/[,$]/g, ''));
    if (Number.isNaN(num) || num < 0) {
      toast.error('Enter a valid positive amount', { position: 'bottom-right' });
      return;
    }
    setSaving(true);
    try {
      await onSave(row.plaidAccountId, num);
      toast.success('Statement balance saved', { position: 'bottom-right' });
      setEditing(false);
    } catch {
      toast.error('Failed to save', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <td className="px-3 py-1.5 text-right" style={{ backgroundColor: '#E8F0FE' }}>
        <div className="flex items-center justify-end gap-1">
          <input
            autoFocus
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="w-28 border border-gray-300 bg-white px-2 py-0.5 text-right text-sm tabular-nums focus:outline-none"
          />
          {saving ? (
            <span className="text-xs text-gray-500">...</span>
          ) : (
            <>
              <button type="button" onClick={handleSave}><CheckIcon className="w-3 h-3 shrink-0" /></button>
              <button type="button" onClick={() => setEditing(false)}><XMarkIcon className="w-3 h-3 shrink-0" /></button>
            </>
          )}
        </div>
      </td>
    );
  }

  return (
    <td
      className={`group px-3 py-2 text-right tabular-nums text-sm ${isCreditType ? 'value-negative' : ''}`}
      style={{ backgroundColor: '#E8F0FE' }}
    >
      <span className="inline-flex items-center gap-1.5">
        {isCreditType
          ? `(${formatCurrencyFull(row.statementBalance).replace('-', '')})`
          : formatCurrencyFull(row.statementBalance)}
        <button type="button" onClick={startEdit} className="opacity-0 group-hover:opacity-60 hover:!opacity-100" title="Edit statement balance">
          <PencilIcon className="w-3 h-3 shrink-0" />
        </button>
      </span>
    </td>
  );
}

function CashflowTable({ tableRows, label, showLiabilities, onSaveStatementBalance }) {
  const isCCTable = tableRows.some((r) => r.type === 'credit');
  const sortedRows = isCCTable
    ? [...tableRows].sort((a, b) => (a.paymentDueDay ?? 999) - (b.paymentDueDay ?? 999))
    : tableRows;

  return (
    <div className="mb-6">
      <div className="entity-header mb-2">{label}</div>
      <div className="overflow-x-auto">
        <table className="eadin-table">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Type</th>
              <th className="!text-right">Available Balance</th>
              <th className="!text-right">Statement Balance <span className="text-[10px] font-normal">(editable)</span></th>
              <th className="!text-right">Credit Limit</th>
              <th className="!text-right">Remaining Limit</th>
              {showLiabilities && isCCTable && (
                <>
                  <th className="!text-right">Next Due Date</th>
                  <th className="!text-right">Last Payment</th>
                  <th className="!text-right">Last Payment Date</th>
                </>
              )}
              <th className="!text-right">Last Synced</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const isFlex = row.type === 'credit' && row.accountName.toLowerCase().includes('flex');
              return (
                <tr key={row.plaidAccountId} style={isFlex ? { borderLeft: '3px solid #dc2626', fontWeight: 600 } : {}}>
                  <td className="px-3 py-2 text-sm font-medium">
                    {row.accountName}
                    {row.mask && <span className="ml-1.5 text-xs text-gray-500">···{row.mask}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 capitalize">{row.subtype ?? row.type}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sm">{formatCurrencyFull(row.availableBalance)}</td>
                  <StatementBalanceCell row={row} onSave={onSaveStatementBalance} />
                  <td className="px-3 py-2 text-right tabular-nums text-sm">{row.creditLimit !== null ? formatCurrencyFull(row.creditLimit) : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sm">{row.remainingLimit !== null ? formatCurrencyFull(row.remainingLimit) : '—'}</td>
                  {showLiabilities && isCCTable && (
                    <>
                      <td className="px-3 py-2 text-right text-sm">
                        {row.mask === '7c9b' ? formatShortDate(getFlexNextDueDate()) : formatShortDate(row.nextPaymentDueDate)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-sm">
                        {row.lastPaymentAmount != null ? formatCurrencyFull(row.lastPaymentAmount) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-sm">{formatShortDate(row.lastPaymentDate)}</td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right text-xs text-gray-500 tabular-nums">{formatTimestamp(row.lastSyncedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const EffectiveCapitalSummaryCashflowExport = ({ onNavigateTab, onLoadingChange, viewSwitcher }) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLiabilities, setShowLiabilities] = useState(true);
  const [isSyncingLiabilities, setIsSyncingLiabilities] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await effectiveCapitalApi.getCashflowExport();
      setRows(data);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load cashflow export data', { position: 'bottom-right' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const handleSaveStatementBalance = async (plaidAccountId, value) => {
    await effectiveCapitalApi.saveStatementBalance(plaidAccountId, value);
    loadData();
  };

  const handleSyncLiabilities = async () => {
    setIsSyncingLiabilities(true);
    try {
      const result = await effectiveCapitalApi.syncLiabilities();
      toast.success(`Liabilities synced: ${result.synced} CC accounts updated`, { position: 'bottom-right' });
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Liabilities sync failed', { position: 'bottom-right' });
    } finally {
      setIsSyncingLiabilities(false);
    }
  };

  const exportCSV = () => {
    if (!rows.length) {
      toast.error('No data to export', { position: 'bottom-right' });
      return;
    }

    const headers = [
      'Entity', 'Account Name', 'Mask', 'Type', 'Subtype',
      'Available Balance', 'Statement Balance Due', 'Credit Limit', 'Remaining Limit',
      'Next Due Date', 'Last Payment', 'Last Payment Date', 'Last Synced',
    ];

    const csvRows = rows.map((r) => [
      r.entity,
      `"${r.accountName.replace(/"/g, '""')}"`,
      r.mask ?? '',
      r.type,
      r.subtype ?? '',
      r.availableBalance.toFixed(2),
      r.statementBalance.toFixed(2),
      r.creditLimit !== null ? r.creditLimit.toFixed(2) : '',
      r.remainingLimit !== null ? r.remainingLimit.toFixed(2) : '',
      (r.mask === '7c9b' ? getFlexNextDueDate() : r.nextPaymentDueDate ?? ''),
      r.lastPaymentAmount != null ? r.lastPaymentAmount.toFixed(2) : '',
      r.lastPaymentDate ?? '',
      r.lastSyncedAt ? new Date(r.lastSyncedAt).toISOString() : '',
    ]);

    const csv = [headers.join(','), ...csvRows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cashflow-balances-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} accounts for cashflow model`, { position: 'bottom-right' });
  };

  const kingsLootRows = rows.filter((r) => r.entity === 'Kings Loot');
  const kingsHoldingRows = rows.filter((r) => r.entity === 'Kings Holding');

  return (
    <div className="flex flex-col">
      <div className="flex shrink-0 flex-col gap-3 py-3 border-b border-gray-200 mb-4">
        <div>
          {viewSwitcher || <h2 className="text-sm font-bold uppercase tracking-widest">13-Week Cashflow Export</h2>}
          <p className="text-xs text-gray-500">All account balances, CC statement balances, and payment due dates</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
          <button
            type="button"
            onClick={() => setShowLiabilities((v) => !v)}
            className="flex items-center justify-center gap-1.5 border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50"
          >
            {showLiabilities ? 'Hide' : 'Show'} Liability Columns
          </button>
          <button
            type="button"
            onClick={handleSyncLiabilities}
            disabled={isSyncingLiabilities}
            className="flex items-center justify-center gap-1.5 border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 shrink-0 ${isSyncingLiabilities ? 'animate-spin' : ''}`} />
            Sync Liabilities
          </button>
          <button
            type="button"
            onClick={exportCSV}
            disabled={!rows.length || isLoading}
            className="sm:col-span-2 lg:col-span-1 flex items-center justify-center gap-1.5 border bg-primary px-4 rounded-lg py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-5 h-5 shrink-0" />
            Export CSV for Google Sheets
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-500 mt-10">
          <LoaderSpinner height="80" width="80" colorSpinner="black" border="4" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-gray-500">
          <p>No accounts connected yet.</p>
          <button type="button" onClick={() => onNavigateTab?.('connect-accounts')} className="text-xs underline">
            Connect accounts first
          </button>
        </div>
      ) : (
        <>
          <div className="mb-5 border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
            <strong className="text-gray-900">Statement Balance</strong> (blue cells) is auto-populated from Plaid Liabilities — click{' '}
            <strong className="text-gray-900">Sync Liabilities</strong> to refresh. You can also hover any cell and click the pencil icon to enter a manual override.
          </div>
          {kingsLootRows.length > 0 && (
            <CashflowTable
              tableRows={kingsLootRows}
              label="Kings Loot"
              showLiabilities={showLiabilities}
              onSaveStatementBalance={handleSaveStatementBalance}
            />
          )}
          {kingsHoldingRows.length > 0 && (
            <CashflowTable
              tableRows={kingsHoldingRows}
              label="Kings Holding"
              showLiabilities={showLiabilities}
              onSaveStatementBalance={handleSaveStatementBalance}
            />
          )}
        </>
      )}
    </div>
  );
};
